// Reading from Sanity in the browser.
//
// No client library here on purpose. The site makes one request for published
// content and nothing else — no writing, no uploads, no live updates, no login —
// so @sanity/client's ~30kB of machinery for those would be dead weight in every
// visitor's download. The query API is a plain URL, so fetch covers it.
//
// The build scripts still use @sanity/client, where size costs nothing.

import imageUrlBuilder from "@sanity/image-url";

export const projectId = "5xi2bokn";
export const dataset = "production";
const apiVersion = "2024-01-01";

// apicdn is the cached, read-only endpoint — the right one for published content.
const API_HOST = `https://${projectId}.apicdn.sanity.io/v${apiVersion}/data/query/${dataset}`;

export async function fetchContent(query) {
  const response = await fetch(`${API_HOST}?query=${encodeURIComponent(query)}`);

  if (!response.ok) {
    throw new Error(`Sanity responded ${response.status}`);
  }

  const { result } = await response.json();
  return result;
}

// The builder only needs the project coordinates, not a client instance.
const builder = imageUrlBuilder({ projectId, dataset });

// Sanity re-encodes on delivery no matter what — the bare asset URL comes back
// optimised, and there is no parameter that returns the stored bytes. So the
// only question is how hard it compresses, and the answer here is: as little as
// possible. These files were already encoded at q100 by cwebp under BLUEPRINT's
// rules, and a second lossy pass at Sanity's default 75 is what made them soft.
const QUALITY = 100;

export const urlFor = (source) => builder.image(source).auto("format").quality(QUALITY);

// Full-width image at a given size, aspect ratio untouched.
export const imageUrl = (source, width) =>
  source ? urlFor(source).width(width).url() : null;

// Gallery images: original dimensions, no resize, nothing but the quality bump.
// This is what the old site served straight out of public/ — 441KB against the
// 458KB stored, which is a q100 re-encode of a q100 file. No auto format either:
// the stored files are already WebP, so there is nothing to negotiate.
export const fullImageUrl = (source) =>
  source ? builder.image(source).quality(QUALITY).url() : null;

// Cropped to a fixed shape. Sanity crops around the focal point set in the
// Studio, so the subject survives the crop instead of the middle of the frame
// winning by default.
export const croppedImageUrl = (source, width, height) =>
  source ? urlFor(source).width(width).height(height).fit("crop").url() : null;
